import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import DashboardLayout from './components/layout/DashboardLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LoadingSpinner from './components/common/LoadingSpinner';
import AIChatWidget from './components/chat/AIChatWidget';
import CommandCenter from './components/dashboard/CommandCenter';
import AccessibilityWidget from './components/common/AccessibilityWidget';
import FloatingCallWidget from './components/shared/FloatingCallWidget';
import { getApiUrl } from './utils/api';

// Helper to auto-reload page if chunk loading fails due to new deployment hashes
const lazyWithRetry = (importFn) =>
  lazy(async () => {
    const isRefreshed = window.sessionStorage.getItem('chunk_refreshed');
    try {
      const component = await importFn();
      window.sessionStorage.removeItem('chunk_refreshed');
      return component;
    } catch (error) {
      if (!isRefreshed) {
        window.sessionStorage.setItem('chunk_refreshed', 'true');
        window.location.reload();
      }
      throw error;
    }
  });

// Lazy loaded Pages
const Home = lazyWithRetry(() => import('./pages/Home'));
const Services = lazyWithRetry(() => import('./pages/Services'));
const Portfolio = lazyWithRetry(() => import('./pages/Portfolio'));
const About = lazyWithRetry(() => import('./pages/About'));
const Contact = lazyWithRetry(() => import('./pages/Contact'));
const Login = lazyWithRetry(() => import('./pages/Login'));
const Register = lazyWithRetry(() => import('./pages/Register'));
const VerifyEmail = lazyWithRetry(() => import('./pages/VerifyEmail'));
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const Projects = lazyWithRetry(() => import('./pages/Projects'));
const ProjectDetails = lazyWithRetry(() => import('./pages/ProjectDetails'));
const Profile = lazyWithRetry(() => import('./pages/Profile'));
const AdminUsers = lazyWithRetry(() => import('./pages/AdminUsers'));
const AdminProjects = lazyWithRetry(() => import('./pages/AdminProjects'));
const AdminPortfolio = lazyWithRetry(() => import('./pages/AdminPortfolio'));
const AdminContacts = lazyWithRetry(() => import('./pages/AdminContacts'));
const CreateProject = lazyWithRetry(() => import('./pages/CreateProject'));
const EditProject = lazyWithRetry(() => import('./pages/EditProject'));
const Messages = lazyWithRetry(() => import('./pages/Messages'));
const Contracts = lazyWithRetry(() => import('./pages/Contracts'));
const Billing = lazyWithRetry(() => import('./pages/Billing'));
const Notifications = lazyWithRetry(() => import('./pages/Notifications'));
const Feedback = lazyWithRetry(() => import('./pages/Feedback'));
const Testimonials = lazyWithRetry(() => import('./pages/Testimonials'));
const AdminMessages = lazyWithRetry(() => import('./pages/AdminMessages'));
const AdminContracts = lazyWithRetry(() => import('./pages/AdminContracts'));
const AdminNotifications = lazyWithRetry(() => import('./pages/AdminNotifications'));
const AdminFeedback = lazyWithRetry(() => import('./pages/AdminFeedback'));
const AdminBilling = lazyWithRetry(() => import('./pages/AdminBilling'));
const AdminTeam = lazyWithRetry(() => import('./pages/AdminTeam'));
const AdminSettings = lazyWithRetry(() => import('./pages/AdminSettings'));
const AdminAIConversations = lazyWithRetry(() => import('./pages/AdminAIConversations'));
const AdminProposals = lazyWithRetry(() => import('./pages/AdminProposals'));
const AdminSystemUpdates = lazyWithRetry(() => import('./pages/AdminSystemUpdates'));
const AdminMirrors = lazyWithRetry(() => import('./pages/AdminMirrors'));
const AdminSecurity = lazyWithRetry(() => import('./pages/AdminSecurity'));
const MaintenanceMode = lazyWithRetry(() => import('./pages/MaintenanceMode'));
const AdminTickets = lazyWithRetry(() => import('./pages/AdminTickets'));
const ClientTickets = lazyWithRetry(() => import('./pages/ClientTickets'));
const TicketDetails = lazyWithRetry(() => import('./pages/TicketDetails'));
const AdminForms = lazyWithRetry(() => import('./pages/AdminForms'));
const ClientForms = lazyWithRetry(() => import('./pages/ClientForms'));
const FormView = lazyWithRetry(() => import('./pages/FormView'));
const Referrals = lazyWithRetry(() => import('./pages/Referrals'));
const AdminReferrals = lazyWithRetry(() => import('./pages/AdminReferrals'));
const ClientReferrals = lazyWithRetry(() => import('./pages/ClientReferrals'));
const AdminCallLogs = lazyWithRetry(() => import('./pages/AdminCallLogs'));

// Policy Pages
const PrivacyPolicy = lazyWithRetry(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazyWithRetry(() => import('./pages/TermsOfService'));
const CookiePolicy = lazyWithRetry(() => import('./pages/CookiePolicy'));
const DataProtection = lazyWithRetry(() => import('./pages/DataProtection'));


function App() {
  const { loading, isAuthenticated, user } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Global Server-Sent Events (SSE) Listener for Auto-Refresh
  // DEFERRED: Only open connection when authenticated to preserve network resources for login
  React.useEffect(() => {
    if (!isAuthenticated) return;

    // Determine the absolute URL if baseURL is defined, otherwise fall back to relative
    const streamUrl = axios.defaults.baseURL 
      ? `${axios.defaults.baseURL}/api/updates/stream` 
      : '/api/updates/stream';

    const eventSource = new EventSource(streamUrl, { withCredentials: true });

    eventSource.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        
        // If a generic 'global_refresh' or 'new_update' comes in, tell React Query 
        // to silently re-fetch all active data across the entire site
        if (payload.type === 'global_refresh' || payload.type === 'new_update') {
           console.log('🔄 Global Pulse Received: Silently refreshing site data...');
           // This instantly invalidates caches, forcing background refetches without reloading the page
           queryClient.invalidateQueries(); 
           
           // Dispatch a window event so non-query components (like PulseStream) can react natively
           window.dispatchEvent(new Event('global_refresh_trigger'));
        }
      } catch (err) {
        // Ignore ping parse errors
      }
    };

    return () => eventSource.close();
  }, [queryClient, isAuthenticated]);


  // Fetch Global Settings to apply Dynamic Branding and App-level blocks
  const { data: globalSettings } = useQuery(
    'global-settings',
    () => axios.get('/api/settings').then(res => res.data.data),
    { 
      refetchOnWindowFocus: false, 
      staleTime: 10 * 60 * 1000, 
      cacheTime: 30 * 60 * 1000,
      initialData: () => {
        const cached = localStorage.getItem('epic-edge-settings');
        return cached ? JSON.parse(cached) : undefined;
      }
    }
  );


  // Sync to localStorage
  React.useEffect(() => {
    if (globalSettings) {
      localStorage.setItem('epic-edge-settings', JSON.stringify(globalSettings));
    }
  }, [globalSettings]);

  // Capture Referral Code from URL parameters & auto-track clicks
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref') || params.get('by') || params.get('referral');
    if (refCode) {
      const cleanRef = refCode.trim().toUpperCase();
      const existingRef = localStorage.getItem('epicedge_referral');
      localStorage.setItem('epicedge_referral', cleanRef);

      // Track click if new or first time
      if (existingRef !== cleanRef) {
        axios.post('/api/referrals/track-click', { code: cleanRef }).catch(() => {});
      }
    }
  }, [location.search]);

  React.useEffect(() => {
    if (globalSettings?.brandColorHex) {
      const hex = globalSettings.brandColorHex;
      let styleEl = document.getElementById('dynamic-brand-styles');
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'dynamic-brand-styles';
        document.head.appendChild(styleEl);
      }

      // We brute-force whitelist standard EpicEdge classes to respect the dynamic brand color
      // without requiring a full tailwind recompile on the client side.
      styleEl.innerHTML = `
        .text-amber-500 { color: ${hex} !important; }
        .text-amber-600 { color: ${hex} !important; filter: brightness(0.9); }
        .bg-amber-500 { background-color: ${hex} !important; }
        .bg-amber-600 { background-color: ${hex} !important; filter: brightness(0.9); }
        .border-amber-500 { border-color: ${hex} !important; }
        .ring-amber-500 { --tw-ring-color: ${hex} !important; }
        .focus\\:border-amber-500:focus { border-color: ${hex} !important; }
        .focus\\:ring-amber-500:focus { --tw-ring-color: ${hex} !important; }
        
        .hover\\:bg-amber-500:hover { background-color: ${hex} !important; }
        .hover\\:bg-amber-600:hover { background-color: ${hex} !important; filter: brightness(0.9); }
        .hover\\:text-amber-500:hover { color: ${hex} !important; }
        .hover\\:text-amber-600:hover { color: ${hex} !important; filter: brightness(0.9); }
        .hover\\:border-amber-500:hover { border-color: ${hex} !important; }
        
        .peer-checked\\:bg-amber-500:checked ~ .peer { background-color: ${hex} !important; }
        .group:hover .group-hover\\:text-amber-500 { color: ${hex} !important; }
      `;
    }
  }, [globalSettings?.brandColorHex]);

  // Handle Dynamic Favicon
  React.useEffect(() => {
    if (globalSettings?.siteLogoUrl) {
      const logoUrl = globalSettings.siteLogoUrl;
      const fullUrl = logoUrl.startsWith('http') || logoUrl.startsWith('data:') 
        ? logoUrl 
        : getApiUrl(logoUrl);

      // Update standard favicon
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon) {
        favicon.href = fullUrl;
      }

      // Update apple-touch-icons
      const appleIcons = document.querySelectorAll('link[rel="apple-touch-icon"]');
      appleIcons.forEach(icon => {
        icon.href = fullUrl;
      });
      
      console.log('🌐 Favicon updated to:', fullUrl);
    } else {
      // Revert to default
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon) favicon.href = '/logo.png';
      
      const appleIcons = document.querySelectorAll('link[rel="apple-touch-icon"]');
      appleIcons.forEach(icon => {
        icon.href = '/logo.png';
      });
    }
  }, [globalSettings?.siteLogoUrl]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Enforce Maintenance Mode (Block all routes if active, except for admins and login page)
  const isMaintenanceMode = globalSettings?.maintenanceMode === true;
  const isAdminUser = isAuthenticated && user?.role === 'admin';
  const isLoginPage = location.pathname === '/login';

  if (isMaintenanceMode && !isAdminUser && !isLoginPage) {
    return (
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      }>
        <MaintenanceMode />
        <AIChatWidget />
      </Suspense>
    );
  }


  // Check if current route is a dashboard/system route
  const isDashboardRoute = location.pathname.startsWith('/dashboard') ||
    location.pathname.startsWith('/projects') ||
    location.pathname.startsWith('/profile') ||
    location.pathname.startsWith('/admin-') ||
    location.pathname.startsWith('/create-project') ||
    location.pathname.startsWith('/edit-project') ||
    location.pathname.startsWith('/messages') ||
    location.pathname.startsWith('/contracts') ||
    location.pathname.startsWith('/billing') ||
    location.pathname.startsWith('/notifications') ||
    location.pathname.startsWith('/tickets') ||
    location.pathname.startsWith('/forms') ||
    location.pathname.startsWith('/referral-rewards') ||
    location.pathname.startsWith('/admin-referrals') ||
    location.pathname.startsWith('/admin-messages') ||
    location.pathname.startsWith('/admin-contracts') ||
    location.pathname.startsWith('/admin-notifications') ||
    location.pathname.startsWith('/admin-feedback') ||
    location.pathname.startsWith('/admin-billing') ||
    location.pathname.startsWith('/admin-settings') ||
    location.pathname.startsWith('/admin-ai') ||
    location.pathname.startsWith('/admin-proposals') ||
    location.pathname.startsWith('/admin-mirrors') ||
    location.pathname.startsWith('/admin-updates') ||
    location.pathname.startsWith('/feedback') ||
    location.pathname.startsWith('/admin-team');

  // Check if current route is a credential/auth route (Audio 1 Fix)
  const isAuthRoute = location.pathname === '/login' ||
    location.pathname === '/register' ||
    location.pathname === '/verify-email';

  // Unified Render Structure
  return (
    <div className="min-h-screen bg-white dark:bg-onyx-900 flex flex-col transition-colors duration-300">
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      }>
        {isDashboardRoute && isAuthenticated ? (
          <DashboardLayout>
            <Routes>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:id" element={<ProjectDetails />} />
              <Route path="/profile" element={<Profile />} />

              {/* Client Tools routes */}
              <Route path="/messages" element={<Messages />} />
              <Route path="/contracts" element={<Contracts />} />
              <Route path="/tickets" element={<ClientTickets />} />
              <Route path="/tickets/:id" element={<TicketDetails />} />
              <Route path="/forms" element={<ClientForms />} />
              <Route path="/forms/:id" element={<FormView />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/referral-rewards" element={<ClientReferrals />} />

              {/* Strict Admin routes */}
              <Route path="/admin-users" element={<ProtectedRoute adminOnly><AdminUsers /></ProtectedRoute>} />
              <Route path="/admin-referrals" element={<ProtectedRoute adminOnly><AdminReferrals /></ProtectedRoute>} />
              <Route path="/admin-billing" element={<ProtectedRoute adminOnly><AdminBilling /></ProtectedRoute>} />
              <Route path="/admin-settings" element={<ProtectedRoute adminOnly><AdminSettings /></ProtectedRoute>} />
              <Route path="/admin-notifications" element={<ProtectedRoute adminOnly><AdminNotifications /></ProtectedRoute>} />
              <Route path="/admin-team" element={<ProtectedRoute adminOnly><AdminTeam /></ProtectedRoute>} />
              <Route path="/admin-updates" element={<ProtectedRoute adminOnly><AdminSystemUpdates /></ProtectedRoute>} />
              
              {/* Team routes (Admin, Manager, Developer) */}
              <Route path="/admin-projects" element={<ProtectedRoute teamOnly><AdminProjects /></ProtectedRoute>} />
              <Route path="/admin-portfolio" element={<ProtectedRoute teamOnly><AdminPortfolio /></ProtectedRoute>} />
              <Route path="/admin-contacts" element={<ProtectedRoute teamOnly><AdminContacts /></ProtectedRoute>} />
              <Route path="/admin-messages" element={<ProtectedRoute teamOnly><AdminMessages /></ProtectedRoute>} />
              <Route path="/admin-contracts" element={<ProtectedRoute teamOnly><AdminContracts /></ProtectedRoute>} />
              <Route path="/admin-tickets" element={<ProtectedRoute teamOnly><AdminTickets /></ProtectedRoute>} />
              <Route path="/admin-forms" element={<ProtectedRoute teamOnly><AdminForms /></ProtectedRoute>} />
              <Route path="/admin-feedback" element={<ProtectedRoute teamOnly><AdminFeedback /></ProtectedRoute>} />
              <Route path="/admin-ai" element={<ProtectedRoute teamOnly><AdminAIConversations /></ProtectedRoute>} />
              <Route path="/admin-proposals" element={<ProtectedRoute teamOnly><AdminProposals /></ProtectedRoute>} />
              <Route path="/admin-proposals/:id" element={<ProtectedRoute teamOnly><AdminProposals /></ProtectedRoute>} />
              <Route path="/admin-mirrors" element={<ProtectedRoute teamOnly><AdminMirrors /></ProtectedRoute>} />
              <Route path="/admin-security" element={<ProtectedRoute teamOnly><AdminSecurity /></ProtectedRoute>} />
              <Route path="/admin-calls" element={<ProtectedRoute teamOnly><AdminCallLogs /></ProtectedRoute>} />
              <Route path="/create-project" element={<ProtectedRoute teamOnly><CreateProject /></ProtectedRoute>} />
              <Route path="/edit-project/:id" element={<ProtectedRoute teamOnly><EditProject /></ProtectedRoute>} />
            </Routes>
          </DashboardLayout>
        ) : (
          <>
            <Navbar />
            <main className="flex-grow">
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Home />} />
                <Route path="/services" element={<Services />} />
                <Route path="/portfolio" element={<Portfolio />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/refer" element={<Referrals />} />
                <Route path="/referrals" element={<Referrals />} />
                <Route path="/testimonials" element={<Testimonials />} />
                <Route path="/feedback" element={<Feedback />} />

                {/* Policy pages */}
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/data-protection" element={<DataProtection />} />
                <Route
                  path="/login"
                  element={
                    isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
                  }
                />
                <Route
                  path="/register"
                  element={
                    isAuthenticated ? <Navigate to="/dashboard" replace /> : <Register />
                  }
                />
                <Route path="/verify-email" element={<VerifyEmail />} />

                {/* Redirect dashboard routes to login if not authenticated */}
                <Route
                  path="/dashboard"
                  element={
                    isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
                  }
                />
                <Route
                  path="/projects/*"
                  element={
                    isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
                  }
                />
                <Route
                  path="/profile"
                  element={
                    isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
                  }
                />
                <Route
                  path="/admin-/*"
                  element={
                    isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
                  }
                />

                {/* Catch all - redirect to home for unauthenticated, dashboard for authenticated */}
                <Route path="*" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/" replace />} />
              </Routes>
            </main>
            <Footer />
          </>
        )}
      </Suspense>

      {!isDashboardRoute && !isAuthRoute && <AIChatWidget />}
      <FloatingCallWidget />
      <CommandCenter />
      <AccessibilityWidget />
    </div>
  );
}

export default App;
